import { PanelLeft, PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import type { CSSProperties, ReactNode } from 'react'
import { Link } from 'react-router'

import { cn } from '../../utils/cn'
import { Logo } from '../brand/Logo'
import { Button } from '../ui/button'
import { SidebarOpenContext } from './context'
import { type ProductTabItem, ProductTabs } from './ProductTabs'
import { SidebarSeparator } from './SidebarSeparator'

interface ProductSidebarProps<T extends string> {
  /** Expanded; false is the icon rail. */
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The nav landmark's name. */
  label: string
  /** Where the wordmark links; it is drawn unlinked without one. */
  home?: { href: string; label: string }
  /** Named under the wordmark: the customer, or a mode of the sidebar. */
  name?: string
  /** The product strip, hidden with fewer than two tabs. */
  tabs?: ProductTabItem<T>[]
  activeTab?: T
  onSelectTab?: (tab: T) => void
  /** The legal entity behind the active tab, at the foot of the rows. */
  entity?: string
  /** Pinned below the scrolling rows. */
  footer?: ReactNode
  /** Fills a drawer at full width instead of standing as a collapsible rail. */
  drawer?: boolean
  /** False when a top bar carries the wordmark, customer and tabs: the rail is rows alone, toggled from its foot. */
  header?: boolean
  /** The rail's open width; 16rem unless a top bar has taken the header's room. */
  width?: string
  className?: string
  /** The active tab's rows and pitches. */
  children: ReactNode
}

/**
 * Every Cai app's product sidebar: the wordmark, the customer, the product switch and the
 * active product's rows.
 *
 * Collapsing to the icon rail keeps the header still: the identity block holds its expanded
 * width and goes invisible. Group labels fade and give their height back, so the icons sit
 * evenly on the rail.
 */
export function ProductSidebar<T extends string>({
  open,
  onOpenChange,
  label,
  home,
  name,
  tabs = [],
  activeTab,
  onSelectTab,
  entity,
  footer,
  drawer = false,
  header = true,
  width = '16rem',
  className,
  children,
}: ProductSidebarProps<T>) {
  const logo = <Logo size="small" width={64} />
  const toggle = (
    <Button
      variant="ghost"
      size="icon-sm"
      onClick={() => onOpenChange(!open)}
      aria-expanded={open}
      aria-label={open ? 'Collapse navigation' : 'Expand navigation'}
      className="hover:text-ink-900 text-ink-400"
    >
      {header ? (
        <PanelLeft aria-hidden="true" />
      ) : open ? (
        <PanelLeftClose aria-hidden="true" />
      ) : (
        <PanelLeftOpen aria-hidden="true" />
      )}
    </Button>
  )
  // A rail with no header keeps its toggle pinned at the foot, just above the entity line.
  const rail = !header && !drawer
  const entityLine = entity && (
    <p
      className={cn(
        'w-(--sidebar-width) shrink-0 truncate px-4 py-3 text-[10px] leading-4 text-neutral-400 group-data-[collapsible=icon]:invisible',
        !rail && 'mt-auto'
      )}
    >
      {entity}
    </p>
  )

  return (
    <SidebarOpenContext.Provider value={open}>
      <aside
        data-slot="sidebar"
        data-state={open ? 'expanded' : 'collapsed'}
        data-collapsible={open ? '' : 'icon'}
        style={
          { '--sidebar-width': drawer ? '18rem' : width, '--sidebar-width-icon': '3rem' } as CSSProperties
        }
        className={cn(
          'group bg-surface text-ink-600 flex flex-col',
          drawer
            ? 'h-full w-full'
            : // z-10 keeps the rail above an app's fixed bars, which pad themselves clear of it.
              'border-ink-200 sticky top-0 z-10 h-svh w-(--sidebar-width) shrink-0 border-r transition-[width] duration-200 ease-linear data-[collapsible=icon]:w-(--sidebar-width-icon) motion-reduce:transition-none',
          className
        )}
      >
        {header && (
          <div className="flex flex-col gap-3 overflow-hidden p-2">
            <div className="flex min-h-8 items-center justify-end">
              <div className="mr-auto ml-2 group-data-[collapsible=icon]:hidden">
                {home ? (
                  <Link to={home.href} aria-label={home.label} className="block">
                    {logo}
                  </Link>
                ) : (
                  logo
                )}
              </div>
              {toggle}
            </div>
            {/* Holds its expanded width as the rail narrows, so nothing below it moves. */}
            <div className="flex w-[calc(var(--sidebar-width)-(--spacing(4)))] flex-col gap-3 group-data-[collapsible=icon]:invisible">
              {/* ph-no-capture: replay masks input values, not rendered text, and this is a customer's name. */}
              {name && (
                <div className="ph-no-capture text-md text-ink-900 truncate px-2 font-medium capitalize">
                  {name}
                </div>
              )}
              {tabs.length > 1 && activeTab && onSelectTab && (
                <ProductTabs tabs={tabs} active={activeTab} onSelect={onSelectTab} disabled={!open} />
              )}
            </div>
          </div>
        )}

        {/* Scrolls down, collapsed or not, but clips sideways: the entity line keeps the full rail width. */}
        <div className="flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto">
          {header && <SidebarSeparator />}
          <nav aria-label={label} className="flex flex-col">
            {children}
          </nav>
          {!rail && entityLine}
        </div>

        {footer && (
          <>
            <SidebarSeparator />
            <div className="flex flex-col gap-2 p-2">{footer}</div>
          </>
        )}

        {rail && (
          <div className="flex flex-col overflow-hidden">
            <div className="flex p-2">{toggle}</div>
            {entityLine}
          </div>
        )}
      </aside>
    </SidebarOpenContext.Provider>
  )
}
